/**
 * Digital-twin chain for Relationship Manager.
 *
 * Product (PIM) → owned/operated by Legal Entity → Asset → Device.
 * Missing links stay honest-empty. Leftover tenant `dev` is never stamped
 * onto prod. Trekker and Weather compose from their own app-scoped graph.
 */

import type { GraphData, GraphEdge, GraphNode, NodeKind } from './types';

export const TWIN_ROLES = ['product', 'legal_entity', 'asset', 'device'] as const;
export type TwinRole = (typeof TWIN_ROLES)[number];

export const TWIN_ROLE_KIND: Record<TwinRole, NodeKind> = {
  product: 'PimItem',
  legal_entity: 'LegalEntity',
  asset: 'Asset',
  device: 'Device',
};

export const TWIN_ROLE_LABEL: Record<TwinRole, string> = {
  product: 'Product (PIM)',
  legal_entity: 'Legal Entity',
  asset: 'Asset',
  device: 'Device',
};

export const TWIN_EDGE_TYPES = ['owned_by', 'operates', 'fitted_with', 'telematics'] as const;
export type TwinEdgeType = (typeof TWIN_EDGE_TYPES)[number];

export const TWIN_EMPTY_REASON = {
  pim_product_absent: 'pim_product_absent',
  legal_entity_absent: 'legal_entity_absent',
  asset_absent: 'asset_absent',
  device_absent: 'device_absent',
  no_app_selected: 'no_app_selected',
  no_instance_edges: 'no_instance_edges',
} as const;

export type TwinNode = {
  id: string;
  role: TwinRole;
  kind: NodeKind;
  label: string;
  present: boolean;
  empty_reason: string | null;
  app_id: string | null;
  tenant_id: string | null;
  props: Record<string, unknown>;
};

export type TwinEdge = {
  id: string;
  type: TwinEdgeType;
  from: string;
  to: string;
  from_role: TwinRole;
  to_role: TwinRole;
};

export type TwinChain = {
  app_id: string | null;
  tenant_id: string | null;
  nodes: TwinNode[];
  edges: TwinEdge[];
  missing: Array<{ role: TwinRole; empty_reason: string }>;
};

function normalizeToken(value: unknown): string | null {
  if (value == null) return null;
  const token = String(value).trim();
  return token ? token : null;
}

/**
 * Tenant used on RM reads/writes. Leftover `dev` on a prod host is
 * rewritten to the hosted tenant — never stamped as `dev`.
 */
export function honestRelationshipTenant(input: {
  selectedTenant?: string | null;
  hostedTenant?: string | null;
  environment?: string | null;
}): string | null {
  const selected = normalizeToken(input.selectedTenant);
  const hosted = normalizeToken(input.hostedTenant);
  const environment = normalizeToken(input.environment);
  const onProd = environment === 'prod' || hosted === 'prod';

  if (onProd) {
    if (!selected || selected === 'dev') return hosted === 'prod' ? 'prod' : hosted || 'prod';
    return selected;
  }

  return selected || hosted;
}

export function relationshipAuthHeaders(
  token: string,
  tenantId: string | null
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (tenantId) headers['x-tenant-id'] = tenantId;
  return headers;
}

function stripRelPrefix(raw: unknown): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^rel:/, '');
}

function classifyTwinRole(node: GraphNode): TwinRole | null {
  const kind = String(node.kind || '').trim();
  const entityType = stripRelPrefix(node.props?.entity_type || node.props?.type);
  const id = String(node.id || '').toLowerCase();
  const tags = (node.tags || []).map((t) => String(t).toLowerCase());

  if (kind === 'PimItem' || entityType === 'product' || entityType === 'spec' || tags.includes('type:product')) {
    return 'product';
  }
  if (
    kind === 'LegalEntity' ||
    kind === 'Cooperative' ||
    kind === 'CloseCorporation' ||
    kind === 'NonProfit' ||
    kind === 'GovernmentEntity' ||
    kind === 'Estate' ||
    entityType === 'legal_entity' ||
    entityType === 'organization' ||
    entityType === 'farm_operation' ||
    entityType === 'farm'
  ) {
    return 'legal_entity';
  }
  if (
    kind === 'Asset' ||
    kind === 'Built-artifact' ||
    entityType === 'asset' ||
    entityType === 'built_artifact' ||
    id.startsWith('asset:') ||
    id.startsWith('utility_tractor_unit:') ||
    /utility_tractor/.test(entityType)
  ) {
    return 'asset';
  }
  if (
    kind === 'Device' ||
    kind === 'Sensor' ||
    entityType === 'device' ||
    entityType === 'iot_device' ||
    entityType === 'sensor' ||
    id.startsWith('device:') ||
    id.startsWith('trackingco:device:') ||
    /^\d{15}$/.test(id.replace(/^device:/, ''))
  ) {
    return 'device';
  }
  return null;
}

function classifyTwinEdge(rawType: unknown): TwinEdgeType | null {
  const type = stripRelPrefix(rawType);
  if (type === 'owned_by' || type === 'owns') return 'owned_by';
  if (type === 'operates' || type === 'operates_asset' || type === 'operated_by') return 'operates';
  if (type === 'telematics') return 'telematics';
  if (
    type === 'fitted_with' ||
    type === 'has_device' ||
    type === 'mounted_on' ||
    type === 'installed_on' ||
    type === 'monitored_by'
  ) {
    return 'fitted_with';
  }
  return null;
}

function emptyReasonFor(role: TwinRole, appId: string | null, hadEdges: boolean): string {
  if (!appId) return TWIN_EMPTY_REASON.no_app_selected;
  if (role === 'product') return TWIN_EMPTY_REASON.pim_product_absent;
  if (role === 'legal_entity') return TWIN_EMPTY_REASON.legal_entity_absent;
  if (role === 'asset') return TWIN_EMPTY_REASON.asset_absent;
  if (!hadEdges) return TWIN_EMPTY_REASON.no_instance_edges;
  return TWIN_EMPTY_REASON.device_absent;
}

function ghostNode(role: TwinRole, appId: string | null, tenantId: string | null, reason: string): TwinNode {
  return {
    id: `missing:${role}`,
    role,
    kind: TWIN_ROLE_KIND[role],
    label: `${TWIN_ROLE_LABEL[role]} — not linked`,
    present: false,
    empty_reason: reason,
    app_id: appId,
    tenant_id: tenantId,
    props: {
      twin_role: role,
      present: false,
      empty_reason: reason,
      _ui_missing: true,
    },
  };
}

export type TwinEntityRecord = {
  id?: string;
  name?: string;
  entity_type?: string;
  entity_subtype?: string | null;
  template_id?: string | null;
  status?: string;
  app_id?: string | null;
};

export type TwinDeviceRecord = {
  id?: string;
  deviceId?: string;
  imei?: string;
  name?: string;
  alias?: string;
  app_id?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type TwinProductRecord = {
  id?: string;
  name?: string;
  sku?: string;
  app_id?: string | null;
};

export type TwinRecordSources = {
  entities?: TwinEntityRecord[];
  devices?: TwinDeviceRecord[];
  products?: TwinProductRecord[];
};

function extractUuid(raw: unknown): string | null {
  const match = String(raw || '').match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  );
  return match ? match[0].toLowerCase() : null;
}

function classifyEntityRecord(entity: TwinEntityRecord): TwinRole | null {
  const blob = [
    entity.entity_type,
    entity.entity_subtype,
    entity.template_id,
    entity.name,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  if (/legal[_:]|legal_entity|party|company|trust|farm_operation|organization/.test(blob)
    && !/utility_tractor|built_artifact|equipment/.test(blob)) {
    return 'legal_entity';
  }
  if (/utility_tractor|built.artifact|equipment|tractor unit/.test(blob)) {
    return 'asset';
  }
  return null;
}

function recordAppMatches(recordApp: unknown, appId: string | null): boolean {
  const stamped = normalizeToken(recordApp);
  if (!stamped || !appId) return true;
  return stamped === appId;
}

export function recordsToGraphNodes(
  records: TwinRecordSources | null | undefined,
  appId: string | null
): GraphNode[] {
  const nodes: GraphNode[] = [];
  for (const entity of records?.entities || []) {
    if (!recordAppMatches(entity.app_id, appId)) continue;
    const role = classifyEntityRecord(entity);
    if (!role || !entity.id) continue;
    const uuid = extractUuid(entity.id);
    const id = role === 'asset' && uuid
      ? `utility_tractor_unit:${uuid}`
      : role === 'legal_entity'
        ? `legal_entity:${entity.id}`
        : String(entity.id);
    nodes.push({
      id,
      kind: TWIN_ROLE_KIND[role],
      label: entity.name || String(entity.id),
      status: 'ok',
      tags: [appId ? `app:${appId}` : null, 'source:entity_registry', `type:${role}`].filter(Boolean) as string[],
      props: {
        entity_type: role === 'asset' ? 'asset' : 'legal_entity',
        entity_id: entity.id,
        app_id: appId,
        source: 'entity_registry',
        aliases: [entity.id, uuid, id].filter(Boolean),
      },
    });
  }
  for (const device of records?.devices || []) {
    if (!recordAppMatches(device.app_id, appId)) continue;
    const imei = normalizeToken(device.imei)
      || normalizeToken(device.metadata?.imei)
      || (normalizeToken(device.deviceId) && /^\d{15}$/.test(String(device.deviceId)) ? String(device.deviceId) : null);
    const deviceId = normalizeToken(device.deviceId) || normalizeToken(device.id) || imei;
    if (!deviceId && !imei) continue;
    const id = `device:${imei || deviceId}`;
    nodes.push({
      id,
      kind: 'Device',
      label: device.name || (imei ? `IMEI ${imei}` : String(deviceId)),
      status: 'ok',
      tags: [appId ? `app:${appId}` : null, 'source:iot', 'type:device'].filter(Boolean) as string[],
      props: {
        entity_type: 'device',
        entity_id: imei || deviceId,
        imei,
        alias: device.alias || null,
        app_id: appId,
        source: 'iot',
        aliases: [deviceId, imei, device.alias, id].filter(Boolean),
      },
    });
  }
  for (const product of records?.products || []) {
    if (!recordAppMatches(product.app_id, appId)) continue;
    const productId = normalizeToken(product.id) || normalizeToken(product.sku);
    if (!productId) continue;
    nodes.push({
      id: `product:${productId}`,
      kind: 'PimItem',
      label: product.name || product.sku || productId,
      status: 'ok',
      tags: [appId ? `app:${appId}` : null, 'source:pim', 'type:product'].filter(Boolean) as string[],
      props: {
        entity_type: 'product',
        entity_id: productId,
        sku: product.sku || null,
        app_id: appId,
        source: 'pim',
      },
    });
  }
  return nodes;
}

/**
 * Kind spine from template-schema must look like the IKU hubs the
 * Standards sidebar already filters on. A 500 here paints "Failed to load schema".
 */
export function normalizeTemplateSchemaGraph(data: GraphData | null | undefined): GraphData {
  if (!data || !Array.isArray(data.nodes)) return { nodes: [], edges: [] };
  const nodes = data.nodes.map((node) => {
    const isKindHub = node.kind === 'KindSpine' || String(node.id || '').startsWith('kind:');
    if (isKindHub) {
      const entityId = String(node.props?.entity_id || String(node.id || '').replace(/^kind:/, ''));
      return {
        ...node,
        id: `iku:${entityId}`,
        kind: 'IKUCategory' as NodeKind,
        props: { ...(node.props || {}), entity_id: entityId, iku_category: entityId },
      };
    }
    if (node.kind === 'Template') {
      const cat = node.props?.iku_category || node.props?.kind || null;
      return { ...node, props: { ...(node.props || {}), iku_category: cat } };
    }
    return node;
  });
  const rewriteId = (id: string) => (String(id || '').startsWith('kind:') ? `iku:${String(id).slice(5)}` : id);
  const edges = (data.edges || []).map((edge) => ({
    ...edge,
    from: rewriteId(edge.from),
    to: rewriteId(edge.to),
    type: edge.type === 'belongs_to_kind' ? 'belongs_to_iku' : edge.type,
  }));
  return { nodes, edges };
}

function nodeAliases(node: GraphNode): string[] {
  const aliases = [
    node.id,
    node.props?.entity_id,
    ...(Array.isArray(node.props?.aliases) ? node.props.aliases : []),
  ]
    .map((v) => normalizeToken(v))
    .filter(Boolean) as string[];
  const uuid = extractUuid(node.id) || extractUuid(node.props?.entity_id);
  if (uuid) {
    aliases.push(uuid, `utility_tractor_unit:${uuid}`, `asset:${uuid}`, uuid.slice(0, 8));
  }
  return Array.from(new Set(aliases));
}

function appScopedGraph(graph: GraphData | null | undefined, appId: string | null): GraphData {
  if (!graph || !Array.isArray(graph.nodes)) return { nodes: [], edges: [] };
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  if (!appId) return { nodes: graph.nodes, edges };

  const nodeMatches = (node: GraphNode) => {
    const nodeApp = normalizeToken(node.props?.app_id);
    const tagApp = (node.tags || []).find((t) => String(t).startsWith('app:'));
    const tagVal = tagApp ? String(tagApp).slice(4) : null;
    if (!nodeApp && !tagVal) return true;
    return nodeApp === appId || tagVal === appId;
  };

  const nodes = graph.nodes.filter(nodeMatches);
  return {
    nodes,
    edges,
  };
}

export function composeTwinChain(input: {
  appId?: string | null;
  selectedTenant?: string | null;
  hostedTenant?: string | null;
  environment?: string | null;
  graph?: GraphData | null;
  records?: TwinRecordSources | null;
}): TwinChain {
  const appId = normalizeToken(input.appId);
  const tenantId = honestRelationshipTenant({
    selectedTenant: input.selectedTenant,
    hostedTenant: input.hostedTenant,
    environment: input.environment,
  });
  const scoped = appScopedGraph(input.graph, appId);
  const recordNodes = recordsToGraphNodes(input.records, appId);
  const seen = new Set(scoped.nodes.map((n) => n.id));
  const mergedNodes = [...scoped.nodes];
  for (const node of recordNodes) {
    if (seen.has(node.id)) continue;
    seen.add(node.id);
    mergedNodes.push(node);
  }
  const hadEdges = scoped.edges.length > 0;
  const hadRecords = mergedNodes.length > 0;

  const presentByRole = new Map<TwinRole, TwinNode>();
  const aliasToId = new Map<string, string>();
  for (const node of mergedNodes) {
    const role = classifyTwinRole(node);
    if (!role) continue;
    for (const alias of nodeAliases(node)) aliasToId.set(alias, node.id);
    if (presentByRole.has(role)) continue;
    presentByRole.set(role, {
      id: node.id,
      role,
      kind: TWIN_ROLE_KIND[role],
      label: node.label || TWIN_ROLE_LABEL[role],
      present: true,
      empty_reason: null,
      app_id: appId,
      tenant_id: tenantId,
      props: {
        ...(node.props || {}),
        twin_role: role,
        present: true,
        entity_id: node.props?.entity_id || node.id,
      },
    });
  }

  const nodes: TwinNode[] = TWIN_ROLES.map((role) => {
    const present = presentByRole.get(role);
    if (present) return present;
    return ghostNode(role, appId, tenantId, emptyReasonFor(role, appId, hadEdges || hadRecords));
  });

  const idToRole = new Map(nodes.filter((n) => n.present).map((n) => [n.id, n.role]));
  for (const node of nodes) {
    if (!node.present) continue;
    for (const alias of nodeAliases({
      id: node.id,
      kind: node.kind,
      label: node.label,
      status: 'ok',
      tags: [],
      props: node.props,
    })) {
      aliasToId.set(alias, node.id);
    }
  }

  const edges: TwinEdge[] = [];
  for (const edge of scoped.edges) {
    const type = classifyTwinEdge(edge.type);
    if (!type) continue;
    const fromId = aliasToId.get(edge.from) || edge.from;
    const toId = aliasToId.get(edge.to) || edge.to;
    const fromRole = idToRole.get(fromId);
    const toRole = idToRole.get(toId);
    if (!fromRole || !toRole) continue;
    edges.push({
      id: edge.id || `edge:${fromId}->${toId}:${type}`,
      type,
      from: fromId,
      to: toId,
      from_role: fromRole,
      to_role: toRole,
    });
  }

  return {
    app_id: appId,
    tenant_id: tenantId,
    nodes,
    edges,
    missing: nodes.filter((n) => !n.present).map((n) => ({
      role: n.role,
      empty_reason: n.empty_reason || TWIN_EMPTY_REASON.no_instance_edges,
    })),
  };
}

export function twinChainToGraphData(chain: TwinChain): GraphData {
  const nodes: GraphNode[] = chain.nodes.map((node) => ({
    id: node.id,
    kind: node.kind,
    label: node.label,
    status: node.present ? 'ok' : 'unknown',
    tags: [
      node.app_id ? `app:${node.app_id}` : null,
      node.tenant_id ? `tenant:${node.tenant_id}` : null,
      `twin:${node.role}`,
      node.present ? 'present' : 'missing',
    ].filter(Boolean) as string[],
    props: node.props,
  }));

  const presentIds = new Set(chain.nodes.filter((n) => n.present).map((n) => n.id));
  const edges: GraphEdge[] = chain.edges
    .filter((edge) => presentIds.has(edge.from) && presentIds.has(edge.to))
    .map((edge) => ({
      id: edge.id,
      type: edge.type,
      from: edge.from,
      to: edge.to,
      props: { twin_edge: edge.type, category: 'operational' },
      status: 'observed',
    }));

  return { nodes, edges };
}

export const TREKKER_APP_ID = 'app_g2U0gH20uTO3';
export const WEATHER_APP_ID = 'app_qkzzBGS81pZ0';
export const TREKKER_ASSET_ID = 'utility_tractor_unit:f432aeed-f4fb-41ec-bfb5-593cdcb06a2d';
export const TREKKER_IMEI = '350317173146469';
export const TREKKER_DEVICE_ALIAS = 'trackingco:device:4567';
